import XIcon from "@heroicons/react/outline/XIcon";
import React, { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";

const theme = {
  EL: {
    headerText: "text-gray-700",
    headerBg: "bg-[#a9fb24]",
    closeBtn: "bg-[#a9fb24] hover:bg-opacity-80 hover:shadow-lg text-gray-700",
  },
  K1: {
    headerText: "text-gray-700",
    headerBg: "bg-yellow-400",
    closeBtn:
      "align-bottom inline-flex font-bold items-center justify-center cursor-pointer leading-5 transition-colors duration-150  focus:outline-none px-5 py-3 rounded-lg text-lg text-white bg-primary-violet hover:shadow-lg hover:bg-dark-violet",
  },
  MS: {
    headerText: "text-white",
    headerBg: "bg-site-teal",
    closeBtn:
      "bg-primary-violet border border-transparent hover:bg-dark-violet",
  },
};

interface IQuestionExplanationPopupProps {
  showPopUp: (show: boolean) => void;
  videoUrl: string;
  vttUrl: string | null;
  textExplanation: string;
  studentType: string;
  itemRef: any;
}

const QuestionExplanationPopup = (props: IQuestionExplanationPopupProps) => {
  const [arrayOfComponents, setArrayOfComponents] = useState<Array<any>>();
  const [containsMath, setContainsMath] = useState<boolean>(false);
  type objectType = {
    type: string;
    object: any;
  };
  const themeName = props.studentType;

  const showPopUpRef = useRef(props.showPopUp);
  useEffect(() => {
    showPopUpRef.current = props.showPopUp;
  });

  const dialogRef = useRef<HTMLDivElement>(null);

  // Handle escape key — attached once on mount via stable ref.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const doc: any = document;
        const isFullscreen = !!(
          document.fullscreenElement ||
          doc.webkitFullscreenElement ||
          doc.mozFullScreenElement ||
          doc.msFullscreenElement
        );
        if (isFullscreen) {
          return;
        }
        showPopUpRef.current(false);
      }
    };
    const handleFullscreenChange = () => {
      const doc: any = document;
      const isFullscreen = !!(
        document.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement
      );
      if (!isFullscreen) {
        dialogRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  useEffect(() => {
    if (props.textExplanation) {
      const mainArray: objectType[] = [];
      const obj: objectType = {
        type: "text",
        object: props.textExplanation,
      };
      mainArray.push(obj);
      setArrayOfComponents(mainArray);
    }
  }, []);

  useEffect(() => {
    if (
      props.textExplanation.includes("<math>") ||
      props.textExplanation.includes("\\(")
    ) {
      window["LearnosityApp"]?.renderMath();
    }
  }, [arrayOfComponents]);
  return (
    <div
      className="fixed inset-0 flex items-center bg-black bg-opacity-50 sm:items-center sm:justify-center"
      style={{ zIndex: 9999 }}
      // Click outside the modal to close it.
      onClick={(e) => {
        if (e.target === e.currentTarget) props.showPopUp(false);
      }}
    >
      <div className="hidden main-content">
        <div id="learnosity-author"></div>
      </div>

      <div
        ref={dialogRef}
        tabIndex={-1}
        className="xs:w-[100%] flex flex-col overflow-y-auto overflow-x-hidden xs:h-auto bg-white dark:bg-gray-800 rounded-lg sm:m-4 sm:w-[75%] max-h-[90vh] outline-none"
        role="dialog"
        aria-modal="true"
        aria-label="Question Explanation"
      >
        {/* Header */}
        <header>
          <div className={` ${theme[themeName]?.headerBg}  px-4 py-4 sm:px-4 `}>
            <div className="flex items-start justify-between space-x-3">
              <div className="space-y-1">
                <div
                  className={` ${theme[themeName]?.headerText} text-lg font-medium `}
                ></div>
              </div>

              <div className="h-7 flex items-center">
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-500"
                  onClick={() => props.showPopUp(false)}
                >
                  <span className="sr-only">Close panel</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {props.videoUrl && props.videoUrl.length > 0 && (
          <div className="w-full relative flex-shrink-0 px-8 py-4">
            <div className="relative w-full aspect-video">
              <div className="absolute inset-0">
                <ReactPlayer
                  url={props.videoUrl}
                  className="absolute top-0 left-0"
                  width="100%"
                  height="100%"
                  controls={true}
                  config={{
                    file: {
                      attributes: {
                        crossOrigin: "anonymous",
                      },
                      tracks: [
                        {
                          kind: "subtitles",
                          src: props.vttUrl || "",
                          srcLang: "en",
                          label: "English",
                          default: true,
                        },
                      ],
                    },
                  }}
                />
              </div>
            </div>
          </div>
        )}
        {props.textExplanation && props.textExplanation.length > 0 && (
          <div className="flex flex-col w-full h-full relative overflow-auto px-8 py-6 ">
            <div>
              <div className="text-lg text-gray-700 font-light bg-blue-50 p-8 flex w-full flex-grow rounded-md rounded-t-lg sm:rounded-lg ">
                {containsMath && (
                  <div className="mathdiv flex-wrap">
                    {arrayOfComponents?.map((element, index) => {
                      return (
                        <>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: element.object ?? "",
                            }}
                          ></span>
                        </>
                      );
                    })}
                  </div>
                )}
                {!containsMath &&
                  arrayOfComponents?.map((element, index) => {
                    return (
                      <>
                        {element.type === "text" && (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: element.object ?? "",
                            }}
                          ></div>
                        )}
                      </>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionExplanationPopup;
